<template>
    <div class="table-question" :class="{ 'is-desktop': desktop }">
        <label
            class="question-label"
            v-bind:class="{ error: valid === false, valid: valid === true }"
            ><slot name="label"></slot
        ></label>

        <!-- Desktop: a real table, typed into like a spreadsheet. -->
        <div
            v-if="desktop"
            class="tq-scroll"
            :style="maxWidth ? { maxWidth: maxWidth + 'px' } : null"
        >
            <table class="tq-table">
                <thead>
                    <tr>
                        <th v-if="fixed" class="tq-rowlabel tq-sticky"></th>
                        <th
                            v-for="(column, j) in columns"
                            :key="'h' + j"
                            :class="headerClass(column, j)"
                            :style="{ minWidth: minWidth(column) + 'px' }"
                            scope="col"
                        >
                            {{ column.label }}
                            <span v-if="column.required" class="tq-required">*</span>
                        </th>
                        <th v-if="!fixed" class="tq-actions-head"></th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(row, i) in rows" :key="'r' + rowKeys[i]">
                        <th v-if="fixed" class="tq-rowlabel tq-sticky" scope="row">
                            {{ itemLabel(i) }}
                        </th>
                        <td
                            v-for="(column, j) in columns"
                            :key="'c' + j"
                            :class="cellClass(i, j)"
                        >
                            <span
                                v-if="isReadonly(i, j)"
                                class="tq-static"
                                :title="formulaTitle(i, j)"
                                >{{ display(i, j) }}</span
                            >
                            <select
                                v-else-if="column.type === 'select'"
                                v-model="row[j]"
                                :aria-label="ariaLabel(i, j)"
                                @focus="onFocus(i, j)"
                                @keydown="onKeydown($event, i, j)"
                            >
                                <option :value="null"></option>
                                <option
                                    v-for="option in activeOptions(column)"
                                    :key="option.text"
                                    :value="option.text"
                                >
                                    {{ option.text }}
                                </option>
                            </select>
                            <input
                                v-else
                                v-model="row[j]"
                                :type="column.type === 'date' ? 'date' : 'text'"
                                :inputmode="column.type === 'number' ? 'decimal' : null"
                                :maxlength="column.type === 'text' ? 500 : null"
                                :class="{ 'tq-number': column.type === 'number' }"
                                :aria-label="ariaLabel(i, j)"
                                :data-cell="i + ':' + j"
                                @focus="onFocus(i, j)"
                                @keydown="onKeydown($event, i, j)"
                                @paste="onPaste($event, i, j)"
                            />
                            <span v-if="affix(column, 'suffix') && !isReadonly(i, j)" class="tq-affix">{{
                                column.suffix
                            }}</span>
                        </td>
                        <td v-if="!fixed" class="tq-actions">
                            <button
                                type="button"
                                class="tq-icon"
                                :title="t('question-table_duplicate')"
                                :aria-label="t('question-table_duplicate')"
                                :disabled="!canAdd"
                                @click="duplicateRow(i)"
                            >
                                &#x2398;
                            </button>
                            <button
                                type="button"
                                class="tq-icon"
                                :title="t('question-table_remove')"
                                :aria-label="t('question-table_remove')"
                                :disabled="rows.length <= 1"
                                @click="removeRow(i)"
                            >
                                &times;
                            </button>
                        </td>
                    </tr>
                </tbody>
                <tfoot v-if="summaryRows.length">
                    <tr v-for="(summary, k) in summaryRows" :key="'s' + k">
                        <th v-if="fixed" class="tq-summary-label tq-sticky" scope="row">
                            {{ summary.label }}
                        </th>
                        <td
                            v-for="(column, j) in columns"
                            :key="'sc' + j"
                            :class="summaryCellClass(k, j)"
                        >
                            <span
                                v-if="!fixed && j === 0 && !summaryHasValue(k, 0)"
                                class="tq-summary-label"
                                >{{ summary.label }}</span
                            >
                            <span v-else class="tq-static" :title="summaryTitle(k, j)">{{
                                summaryDisplay(k, j)
                            }}</span>
                        </td>
                        <td v-if="!fixed"></td>
                    </tr>
                </tfoot>
            </table>
        </div>

        <!-- Phone: one card per row. -->
        <div v-else class="tq-cards">
            <div class="tq-card" v-for="(row, i) in rows" :key="'m' + rowKeys[i]">
                <div class="tq-card-head">
                    <span>{{ fixed ? itemLabel(i) : t("question-table_row", { n: i + 1 }) }}</span>
                    <button
                        v-if="!fixed && rows.length > 1"
                        type="button"
                        class="tq-icon"
                        :aria-label="t('question-table_remove')"
                        @click="removeRow(i)"
                    >
                        &times;
                    </button>
                </div>
                <div class="tq-card-fields">
                    <div
                        v-for="(column, j) in columns"
                        :key="'mf' + j"
                        class="tq-field"
                        :class="mobileFieldClass(i, j, column)"
                        v-show="!isFormulaCell(i, j)"
                    >
                        <span class="tq-field-label">
                            {{ column.label }}<span v-if="column.required" class="tq-required">*</span>
                        </span>
                        <span v-if="isReadonly(i, j)" class="tq-static">{{ display(i, j) }}</span>
                        <select v-else-if="column.type === 'select'" v-model="row[j]">
                            <option :value="null"></option>
                            <option
                                v-for="option in activeOptions(column)"
                                :key="option.text"
                                :value="option.text"
                            >
                                {{ option.text }}
                            </option>
                        </select>
                        <input
                            v-else
                            v-model="row[j]"
                            :type="column.type === 'date' ? 'date' : 'text'"
                            :inputmode="column.type === 'number' ? 'decimal' : null"
                            :maxlength="column.type === 'text' ? 500 : null"
                        />
                    </div>
                </div>
                <div class="tq-card-foot" v-if="formulaColumns(i).length">
                    <span v-for="j in formulaColumns(i)" :key="'mff' + j">
                        {{ columns[j].label }}: <strong>{{ display(i, j) || "–" }}</strong>
                    </span>
                </div>
            </div>
            <div class="tq-card tq-card-summary" v-for="(summary, k) in summaryRows" :key="'ms' + k">
                <div class="tq-card-head">{{ summary.label }}</div>
                <div class="tq-card-foot">
                    <span v-for="j in summaryColumns(k)" :key="'msc' + j">
                        {{ columns[j].label }}: <strong>{{ summaryDisplay(k, j) || "–" }}</strong>
                    </span>
                </div>
            </div>
        </div>

        <button
            v-if="!fixed"
            type="button"
            class="tq-add"
            :disabled="!canAdd"
            @click="addRow(true)"
        >
            + {{ addLabel }}
        </button>

        <div class="question-error" v-if="valid === false && error">
            {{ error }}
        </div>
        <div class="tq-notice" v-if="notice">{{ notice }}</div>
    </div>
</template>
<script>
import viewport from "../../utils/viewport";
import {
    computeTable,
    cellConfig,
    summaryCellConfig,
    hasFormula,
    isLockedConfig,
    isRowFilled,
    columnLetter,
} from "../../utils/tableFormula";

const DEFAULT_MIN_WIDTH = { number: 64, date: 120, select: 120, text: 160 };

let keySeq = 0;
const nextKey = () => {
    keySeq += 1;
    return keySeq;
};

export default {
    name: "table-question",
    props: {
        settings: {
            type: Object,
            default: () => ({}),
        },
    },
    data() {
        return {
            rows: [],
            rowKeys: [],
            invalidCells: {},
            error: null,
            valid: null,
            notice: null,
        };
    },
    created() {
        if (this.fixed) {
            this.rows = this.items.map((item, i) => this.blankRow(i));
        } else {
            const start = Math.max(this.minRows, 1);
            for (let i = 0; i < start; i++) {
                this.rows.push(this.blankRow(0));
            }
        }
        this.rowKeys = this.rows.map(() => nextKey());
    },
    computed: {
        desktop() {
            return viewport.desktop;
        },
        columns() {
            return (this.settings && this.settings.columns) || [];
        },
        rowsConfig() {
            return (this.settings && this.settings.rows) || {};
        },
        fixed() {
            return this.rowsConfig.mode === "fixed";
        },
        items() {
            return this.rowsConfig.items || [];
        },
        summaryRows() {
            return this.rowsConfig.summary || [];
        },
        minRows() {
            const min = parseInt(this.rowsConfig.min, 10);
            return isNaN(min) ? 1 : Math.max(0, min);
        },
        maxRows() {
            const max = parseInt(this.rowsConfig.max, 10);
            return Math.min(isNaN(max) ? 50 : max, 200);
        },
        canAdd() {
            return !this.fixed && this.rows.length < this.maxRows;
        },
        addLabel() {
            return this.rowsConfig.addLabel || this.t("question-table_add_row");
        },
        // Optional: by default the table uses the whole width of the form.
        maxWidth() {
            return parseInt(this.settings && this.settings.maxWidth, 10) || null;
        },
        parsedRows() {
            return this.rows.map((row) =>
                row.map((value, j) => this.parseCell(value, this.columns[j])),
            );
        },
        // Dynamic rows with nothing typed are not part of the answer: they are
        // left out of the calculations exactly like the server does.
        filledIndexes() {
            if (this.fixed) {
                return this.parsedRows.map((row, i) => i);
            }
            return this.parsedRows
                .map((row, i) => (isRowFilled(this.settings, row) ? i : -1))
                .filter((i) => i !== -1);
        },
        computed() {
            const input = this.filledIndexes.map((i) => this.parsedRows[i]);
            try {
                return computeTable(this.settings, input);
            } catch {
                return { values: input, summary: [], errors: {} };
            }
        },
        computedRowOf() {
            const map = {};
            this.filledIndexes.forEach((rowIndex, k) => {
                map[rowIndex] = k;
            });
            return map;
        },
    },
    methods: {
        blankRow(itemIndex) {
            return this.columns.map((column, j) => {
                const cfg = this.configAt(itemIndex, j);
                if (cfg && cfg.value !== undefined && cfg.value !== null && !hasFormula(cfg)) {
                    return this.formatInput(cfg.value, column);
                }
                return column.type === "select" ? null : "";
            });
        },
        configAt(rowIndex, j) {
            return cellConfig(this.settings, this.fixed ? rowIndex : 0, j);
        },
        isFormulaCell(i, j) {
            return hasFormula(this.configAt(i, j));
        },
        isReadonly(i, j) {
            return isLockedConfig(this.configAt(i, j));
        },
        activeOptions(column) {
            return (column.options || []).filter((o) => o && !o.removed);
        },
        minWidth(column) {
            return parseInt(column.minWidth, 10) || DEFAULT_MIN_WIDTH[column.type] || 120;
        },
        itemLabel(i) {
            return (this.items[i] && this.items[i].label) || "";
        },
        affix(column, which) {
            return column.type === "number" && column[which];
        },
        ariaLabel(i, j) {
            const row = this.fixed ? this.itemLabel(i) : this.t("question-table_row", { n: i + 1 });
            return this.columns[j].label + ", " + row;
        },
        headerClass(column, j) {
            return {
                "tq-number": column.type === "number",
                "tq-sticky": !this.fixed && j === 0,
            };
        },
        cellClass(i, j) {
            return {
                "tq-cell": true,
                "tq-sticky": !this.fixed && j === 0,
                "tq-readonly": this.isReadonly(i, j),
                "tq-formula": this.isFormulaCell(i, j),
                "tq-invalid": !!this.invalidCells[i + ":" + j],
                "tq-has-prefix": !!this.affix(this.columns[j], "prefix"),
                "tq-number": this.columns[j].type === "number",
            };
        },
        summaryCellClass(k, j) {
            return {
                "tq-cell": true,
                "tq-summary": true,
                "tq-sticky": !this.fixed && j === 0,
                "tq-number": this.columns[j].type === "number",
            };
        },
        mobileFieldClass(i, j, column) {
            return {
                "tq-field-number": column.type === "number",
                "tq-invalid": !!this.invalidCells[i + ":" + j],
            };
        },
        formulaColumns(i) {
            return this.columns
                .map((c, j) => j)
                .filter((j) => this.isFormulaCell(i, j));
        },
        summaryColumns(k) {
            return this.columns
                .map((c, j) => j)
                .filter((j) => hasFormula(summaryCellConfig(this.settings, k, j)));
        },
        summaryHasValue(k, j) {
            return hasFormula(summaryCellConfig(this.settings, k, j));
        },

        // ------------------------------------------------------ values
        parseCell(value, column) {
            if (!column) return null;
            if (value === null || value === undefined) return null;
            if (column.type === "number") {
                if (typeof value === "number") return value;
                const text = String(value).trim().replace(/\s/g, "");
                if (text === "") return null;
                const normalized = text.indexOf(",") !== -1 ? text.replace(/\./g, "").replace(",", ".") : text;
                const number = Number(normalized);
                return isFinite(number) ? number : text;
            }
            if (column.type === "text") {
                const text = String(value).trim();
                return text === "" ? null : text;
            }
            return value === "" ? null : value;
        },
        formatInput(value, column) {
            if (column.type === "number" && typeof value === "number") {
                return String(value).replace(".", ",");
            }
            return value;
        },
        formatNumber(value, column) {
            if (value === null || value === undefined || value === "") return "";
            if (typeof value !== "number") return String(value);
            const decimals = parseInt(column.decimals, 10);
            let text = isNaN(decimals) ? String(parseFloat(value.toPrecision(12))) : value.toFixed(decimals);
            text = text.replace(".", ",");
            if (column.prefix) text = column.prefix + " " + text;
            if (column.suffix) text = text + " " + column.suffix;
            return text;
        },
        display(i, j) {
            const column = this.columns[j];
            const k = this.computedRowOf[i];
            let value;
            if (k === undefined) {
                // Empty dynamic row: show the locked value, never a result.
                const cfg = this.configAt(i, j);
                value = cfg && !hasFormula(cfg) ? cfg.value : null;
            } else {
                value = this.computed.values[k] ? this.computed.values[k][j] : null;
            }
            if (column.type === "number") return this.formatNumber(value, column);
            return value === null || value === undefined ? "" : String(value);
        },
        formulaTitle(i, j) {
            const k = this.computedRowOf[i];
            const error = k === undefined ? null : this.computed.errors[k + ":" + j];
            return error ? this.t("question-table_formula_error") : null;
        },
        summaryDisplay(k, j) {
            // Nothing typed yet: a row of zeros is just noise.
            if (!this.fixed && !this.filledIndexes.length) return "";
            const row = this.computed.summary[k] || [];
            // Same rule as the answer text: a zero total is left blank.
            if (row[j] === 0) return "";
            return this.formatNumber(row[j], this.columns[j]);
        },
        summaryTitle(k, j) {
            return this.computed.errors["s:" + k + ":" + j] ? this.t("question-table_formula_error") : null;
        },

        // ------------------------------------------------------ rows
        addRow(focus) {
            if (!this.canAdd) return false;
            this.rows.push(this.blankRow(0));
            this.rowKeys.push(nextKey());
            if (focus) {
                this.$nextTick(() => this.focusCell(this.rows.length - 1, this.firstEditable()));
            }
            return true;
        },
        duplicateRow(i) {
            if (!this.canAdd) return;
            this.rows.splice(i + 1, 0, this.rows[i].slice());
            this.rowKeys.splice(i + 1, 0, nextKey());
        },
        removeRow(i) {
            if (this.rows.length <= 1) return;
            this.rows.splice(i, 1);
            this.rowKeys.splice(i, 1);
            this.invalidCells = {};
        },
        firstEditable() {
            for (let j = 0; j < this.columns.length; j++) {
                if (!this.isReadonly(0, j)) return j;
            }
            return 0;
        },
        focusCell(i, j) {
            const el = this.$el.querySelector("[data-cell=\"" + i + ":" + j + "\"]");
            if (el) el.focus();
        },

        // ------------------------------------------------------ keyboard
        // No "focus" event: on a phone it would scroll the page back to the
        // top of the table on every cell.
        onFocus() {},
        onKeydown(event, i, j) {
            if (event.key !== "Enter" || event.shiftKey || event.altKey) return;
            // Enter goes down like a spreadsheet and never submits the form.
            event.preventDefault();
            if (i + 1 < this.rows.length) {
                this.focusCell(i + 1, j);
            } else if (this.addRow(false)) {
                this.$nextTick(() => this.focusCell(i + 1, j));
            }
        },
        onPaste(event, i, j) {
            const data = event.clipboardData && event.clipboardData.getData("text/plain");
            if (!data || (data.indexOf("\t") === -1 && data.indexOf("\n") === -1)) {
                return;
            }
            event.preventDefault();
            const lines = data.replace(/\r/g, "").replace(/\n$/, "").split("\n");
            let skipped = 0;
            lines.forEach((line, di) => {
                const r = i + di;
                if (r >= this.rows.length && !this.addRow(false)) {
                    skipped += 1;
                    return;
                }
                let c = j;
                line.split("\t").forEach((value) => {
                    while (c < this.columns.length && this.isReadonly(r, c)) c++;
                    if (c >= this.columns.length) return;
                    this.$set(this.rows[r], c, value.trim());
                    c++;
                });
            });
            this.notice = skipped ? this.t("question-table_paste_skipped", { n: skipped }) : null;
        },

        // ------------------------------------------------------ question API
        isValid(fromChange) {
            // While typing, only re-check once errors are already on screen.
            if (fromChange && this.valid !== false) {
                return true;
            }
            const invalid = {};
            let message = null;
            const parsed = this.parsedRows;
            const filled = this.filledIndexes;

            filled.forEach((i) => {
                this.columns.forEach((column, j) => {
                    if (this.isReadonly(i, j)) return;
                    const value = parsed[i][j];
                    const raw = this.rows[i][j];
                    if (value === null) {
                        if (column.required) invalid[i + ":" + j] = "required";
                        return;
                    }
                    if (column.type === "number") {
                        if (typeof value !== "number") {
                            invalid[i + ":" + j] = "number";
                            return;
                        }
                        // Blank min/max/decimals mean "no limit", like the server.
                        const blank = (v) => v === null || v === undefined || v === "";
                        if (!blank(column.min) && value < Number(column.min)) invalid[i + ":" + j] = "range";
                        if (!blank(column.max) && value > Number(column.max)) invalid[i + ":" + j] = "range";
                        if (!blank(column.decimals)) {
                            const decimals = parseInt(column.decimals, 10) || 0;
                            const fraction = String(raw).split(/[.,]/)[1] || "";
                            if (fraction.length > decimals) invalid[i + ":" + j] = "decimals";
                        }
                    }
                    if (column.type === "select") {
                        const ok = this.activeOptions(column).some((o) => o.text === value);
                        if (!ok) invalid[i + ":" + j] = "select";
                    }
                    if (column.type === "date" && !/^\d{4}-\d{2}-\d{2}$/.test(String(value))) {
                        invalid[i + ":" + j] = "date";
                    }
                    if (column.type === "text" && String(value).length > 500) {
                        invalid[i + ":" + j] = "text";
                    }
                });
            });

            const count = Object.keys(invalid).length;
            if (!this.fixed && filled.length < this.minRows) {
                message = this.t("question-table_min_rows", { n: this.minRows });
            } else if (!this.fixed && filled.length > this.maxRows) {
                message = this.t("question-table_max_rows", { n: this.maxRows });
            } else if (count) {
                const firstKey = Object.keys(invalid)[0];
                const [r, c] = firstKey.split(":").map(Number);
                const where = this.fixed ? this.itemLabel(r) : this.t("question-table_row", { n: r + 1 });
                message = this.t("question-table_invalid_cells", {
                    n: count,
                    where: this.columns[c].label + " (" + where + ")",
                });
            }

            this.invalidCells = invalid;
            this.error = message;
            this.valid = !message;
            return this.valid;
        },
        firstInvalidElement() {
            const key = Object.keys(this.invalidCells)[0];
            if (!key) return null;
            return this.$el.querySelector("[data-cell=\"" + key + "\"]");
        },
        getValue() {
            return {
                values: this.parsedRows.map((row) => row.slice()),
            };
        },
        isNull() {
            return this.filledIndexes.length === 0;
        },
        cellRef(i, j) {
            return columnLetter(j) + (i + 1);
        },
    },
    watch: {
        rows: {
            deep: true,
            handler() {
                this.$emit("change", this.getValue());
            },
        },
    },
};
</script>
